import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DynamicFormService } from '@ntap/common';
import { PrimeNGInputDropdownComponent } from '@ntap/primeng';
import {
  BehaviorSubject,
  filter,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { type IntegrationSearchResult } from './integration-search.component';

type GeonamesSearchResult = IntegrationSearchResult & {
  lat: number;
  lng: number;
  vrstaMjestaId: number;
  country: string;
  koordinatniSustav: string;
};

@Component({
  selector: 'app-geonames-integration',
  template: `
    <ng-container *ngIf="inputConfig$ | async as inputConfig">
      <app-integration-search
        [inputConfig]="inputConfig"
        [control]="geonamesLocationFormContol"
        (searchResult)="onSearchResult($event)"
        searchType="geonames"
        displayLabel="label"
        subheaderLabel="country"
        linkTooltip="Pregled lokacije na Geonames web stranici."
        placeholder="Geonames lokacija"
        linkBase="https://www.geonames.org"
      >
      </app-integration-search>
    </ng-container>
  `
})
export default class GeonamesIntegrationInputComponent
  extends PrimeNGInputDropdownComponent
  implements OnInit, OnDestroy
{
  public geonamesLocationFormContol: FormControl;
  public searchResults$ = new BehaviorSubject<GeonamesSearchResult[]>([]);

  private onDestroy$ = new Subject<void>();
  private dynamicFormService = inject(DynamicFormService);

  override ngOnInit(): void {
    const initialValue = this.formControl.value;

    this.geonamesLocationFormContol = new FormControl(
      initialValue,
      Validators.required
    );

    this.geonamesLocationFormContol.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((id) => {
        this.formControl.setValue(id);
        this.formControl.markAsDirty();
      });

    this.dynamicFormService.formGroup$
      .pipe(
        takeUntil(this.onDestroy$),
        switchMap((fg) => {
          return this.geonamesLocationFormContol.valueChanges.pipe(
            switchMap((id) =>
              this.searchResults$.pipe(
                map((results) => results.filter((l) => l.id === id)),
                map((filteredResults) => filteredResults[0]),
                filter((selected) => !!selected),
                tap((selected) => {
                  const newFormFalue = {
                    ...fg.value,
                    'en~mjesto~koordinatni_sustav': selected.koordinatniSustav,
                    'en~mjesto~vrijednost_x': selected.lng,
                    'en~mjesto~vrijednost_y': selected.lat
                  };
                  if (selected.vrstaMjestaId) {
                    // ne pregaziti sto je korisnik upisao ako nismo nasli mapping u geonames
                    newFormFalue['en~mjesto~vrsta_mjesta_id'] =
                      selected.vrstaMjestaId;
                  }
                  fg.setValue(newFormFalue);
                  fg.markAsDirty();
                })
              )
            )
          );
        })
      )
      .subscribe();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onDestroy$.next();
  }

  public onSearchResult(searchResult: IntegrationSearchResult[]) {
    this.searchResults$.next(searchResult as GeonamesSearchResult[]);
  }
}
